import { useState } from "react";
import {
  Card,
  CardBody,
  Chip,
  Button,
  CardHeader,
  CardFooter,
  Divider,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
  useDisclosure,
  Skeleton,
  cn,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { useQueryState, parseAsInteger } from "nuqs";
import {
  Bell,
  Calendar,
  Settings,
  CreditCard,
  Clock,
  Repeat,
  Snowflake,
  Edit,
  Trash2,
} from "lucide-react";
import {
  getLocalizedName,
  getLocalizedValue,
  getTranslatedTimeframe,
} from "@/utils";
import { useClientFeature } from "@/hooks/useClientFeature";
import { GetCustomerSubscription } from "@/server-types";
import { SubscriptionInvoiceModal } from "@/pages/Dashboard/CustomerSubscriptionOverview/components/SubscriptionInvoiceModal";
import { CustomerSubscriptionCancelModal } from "./CustomerSubscriptionCancelModal";
import { CustomerSubscriptionCommitmentTerm } from "./CustomerSubscriptionCommitmentTerm";
import { CustomerSubscriptionCommitmentsModal } from "./CustomerSubscriptionCommitmentsModal";
import { displayedCommitment } from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import { api, queryClient } from "@/lib/api-client";
import { CustomerSubscriptionEventsModal } from "./CustomerSubscriptionEventsModal";
import { CustomerSubscriptionExtensionModal } from "./CustomerSubscriptionExtensionModal";
import { CustomerSubscriptionNotificationsModal } from "./CustomerSubscriptionNotificationsModal";
import { CustomerSubscriptionFreezeModal } from "./CustomerSubscriptionFreezeModal";
import { CustomerSubscriptionDeleteFreezeModal } from "./CustomerSubscriptionDeleteFreezeModal";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { PrescheduledSelectModal } from "@/Components/AppointmentModal/components/PrescheduledSelectModal";
import { CustomerSubscriptionSettingsModal } from "./CustomerSubscriptionSettingsModal";
import { CustomerSubscriptionAppointmentsModal } from "./CustomerSubscriptionAppointmentsModal";
import { CustomerSubscriptionLinkAppointmentsModal } from "./CustomerSubscriptionLinkAppointmentsModal";
import { CustomerSubscriptionReactivateModal } from "./CustomerSubscriptionReactivateModal";
import { CustomerSubscriptionCreditsSection } from "./CustomerSubscriptionCreditsSection";
import { getUsableLocationLabels } from "./getUsableLocationLabels";

function getFreezeStatusMeta(requestStatus: string | null | undefined) {
  switch (requestStatus) {
    case "pendingReview":
      return { label: t`V pregledu`, color: "warning" as const };
    case "rejected":
      return { label: t`Zavrnjeno`, color: "danger" as const };
    case "withdrawn":
      return { label: t`Preklicano`, color: "default" as const };
    case "cancelled":
      return { label: t`Preklicala stranka`, color: "default" as const };
    default:
      return { label: t`Potrjeno`, color: "success" as const };
  }
}

function getTranslatedLimitationTimeframe(timeframe: string) {
  switch (timeframe.toLowerCase()) {
    case "day":
      return t`Dan`;
    case "week":
      return t`Teden (pon-ned)`;
    case "all":
      return t`Celotno obdobje`;
    default:
      return timeframe;
  }
}

export const getStatusTranslation = (
  status: GetCustomerSubscription["response"]["customerSubscription"]["status"],
) => {
  switch (status) {
    case "active":
      return t`Aktiven`;
    case "expired":
      return t`Potekel`;
    case "frozen":
      return t`Zamrznjen`;
    case "canceled":
      return t`Preklican`;
    default:
      return t`Neznano`;
  }
};

const CustomerSubscriptionCardContent = ({
  customerSubscription,
}: {
  customerSubscription: GetCustomerSubscription["response"]["customerSubscription"];
}) => {
  const { isFeatureEnabled } = useClientFeature();
  const { data: locationsData } = api.location.useGetLocationsByClient();
  const usableLocationLabels = getUsableLocationLabels(
    locationsData?.locations ?? [],
    customerSubscription.locationIds,
  );

  function getLimitationProgressValues(limitation: {
    usageAsOfNow: number;
    usedAmount: number;
    limitAmount: number;
  }) {
    const safeLimit = Math.max(limitation.limitAmount, 0);
    const usageAsOfNow = Math.max(limitation.usageAsOfNow, 0);
    const usedAmount = Math.max(limitation.usedAmount, usageAsOfNow);
    const futureUsage = Math.max(usedAmount - usageAsOfNow, 0);

    if (safeLimit === 0) {
      return {
        usagePercentage: 0,
        totalBookedPercentage: 0,
        futureUsage,
      };
    }

    const usagePercentage = Math.min((usageAsOfNow / safeLimit) * 100, 100);
    const totalBookedPercentage = Math.min((usedAmount / safeLimit) * 100, 100);

    return {
      usagePercentage,
      totalBookedPercentage,
      futureUsage,
    };
  }

  const [invoiceModalSubscriptionId, setInvoiceModalSubscriptionId] =
    useQueryState(
      "invoiceModal",
      parseAsInteger.withOptions({ history: "replace" }),
    );

  const isInvoiceModalOpen =
    invoiceModalSubscriptionId === customerSubscription.id;

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const {
    isOpen: isCommitmentsModalOpen,
    onOpen: onCommitmentsModalOpen,
    onClose: onCommitmentsModalClose,
  } = useDisclosure();

  const {
    isOpen: isReactivateModalOpen,
    onOpen: onReactivateModalOpen,
    onClose: onReactivateModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const {
    isOpen: isExtendModalOpen,
    onOpen: onExtendModalOpen,
    onClose: onExtendModalClose,
  } = useDisclosure();

  const {
    isOpen: isFreezeModalOpen,
    onOpen: onFreezeModalOpen,
    onClose: onFreezeModalClose,
  } = useDisclosure();

  const [eventsModalSubscriptionId, setEventsModalSubscriptionId] =
    useQueryState(
      "eventsModal",
      parseAsInteger.withOptions({ history: "replace" }),
    );

  const isEventsModalOpen =
    eventsModalSubscriptionId === customerSubscription.id;

  const [
    notificationsModalSubscriptionId,
    setNotificationsModalSubscriptionId,
  ] = useQueryState(
    "notificationsModal",
    parseAsInteger.withOptions({ history: "replace" }),
  );

  const isNotificationsModalOpen =
    notificationsModalSubscriptionId === customerSubscription.id;

  const {
    isOpen: settingsModalOpen,
    onOpen: onSettingsModalOpen,
    onClose: onSettingsModalClose,
  } = useDisclosure();

  const {
    isOpen: isLinkAppointmentsModalOpen,
    onOpen: onLinkAppointmentsModalOpen,
    onClose: onLinkAppointmentsModalClose,
  } = useDisclosure();

  const {
    isOpen: isAppointmentsModalOpen,
    onOpen: onAppointmentsModalOpen,
    onClose: onAppointmentsModalClose,
  } = useDisclosure();

  const [selectedFreezeForDelete, setSelectedFreezeForDelete] = useState<
    number | null
  >(null);
  const [scheduledFreezeId, setScheduledFreezeId] = useState<number | null>(
    null,
  );

  const {
    isOpen: isPrescheduledSelectOpen,
    onOpen: onPrescheduledSelectOpen,
    onClose: onPrescheduledSelectClose,
  } = useDisclosure();

  const {
    mutateAsync: postPrescheduledUserAppointment,
    isPending: isPostingPrescheduled,
    processedErrorMessage: prescheduledAppointmentError,
  } = api.appointments.usePostPrescheduledUserAppointment();

  const commitment = displayedCommitment(customerSubscription.commitments);
  // A package with no term yet is exactly where one gets signed; one that has a term
  // still needs its end-of-term decision after the feature is turned back off.
  const canOpenCommitments =
    customerSubscription.commitments.length > 0 ||
    isFeatureEnabled("subscriptionCommitments");

  const discount = customerSubscription.currentPeriodDiscount;
  const discountMaxPeriods =
    discount?.customerSubscriptionDiscount?.maxPeriods ?? null;
  const discountLabel = (() => {
    if (!discount) return null;
    if (discount.discountType === "percentage") {
      return `${discount.discountAmount}%`;
    }
    return discount.discountAmountAbsoluteFormatted;
  })();
  const discountChipLabel = discountLabel
    ? discountMaxPeriods
      ? t`Popust ${discountLabel} za ${discountMaxPeriods} obdobij`
      : t`Popust ${discountLabel}`
    : null;
  const hasActiveDiscount = Boolean(customerSubscription.currentPeriodDiscount);
  const companySplitChipLabel = (() => {
    const { payerCompany, companyShareFormatted } = customerSubscription;
    if (!payerCompany || !companyShareFormatted) return null;
    return t`${payerCompany.companyName} krije ${companyShareFormatted}`;
  })();
  const discountedPriceFormatted =
    customerSubscription.discountedPriceFormatted;
  const {
    mutateAsync: deleteSubscription,
    isPending: isDeletingSubscription,
    processedErrorMessage: deleteSubscriptionError,
  } = api.customer.useDeleteCustomerSubscription();

  const isSubscriptionCanceled = customerSubscription.status === "canceled";
  const isCurrentPeriodCommitted =
    customerSubscription.commitmentLock.chainLockedPeriodIds.includes(
      customerSubscription.currentInvoiceId,
    );
  const canDeleteSubscription = isSubscriptionCanceled;

  const handleDeleteSubscription = async () => {
    try {
      await deleteSubscription({
        customerSubscriptionId: customerSubscription.id,
      });
      addToast({
        title: t`Paket izbrisan`,
        description: t`Paket je bil uspešno izbrisan`,
        color: "success",
      });
      onDeleteModalClose();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: deleteSubscriptionError,
        color: "danger",
      });
    }
  };

  const subscriptionServiceIds =
    customerSubscription.customerSubscriptionServices.map(
      (subscriptionService) => subscriptionService.serviceId,
    );

  const hasServiceLimitations =
    customerSubscription.customerSubscriptionServices.some(
      (subscriptionService) => subscriptionService.limitations.length > 0,
    );
  const hasTotalLimitations = customerSubscription.limitations.length > 0;
  const hasAnyLimitations = hasServiceLimitations || hasTotalLimitations;

  return (
    <>
      <Card
        fullWidth
        shadow="none"
        radius="md"
        className="border border-gray-100"
        data-identifier="subscription-card"
      >
        <CardHeader className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Chip
              data-identifier="subscription-status"
              size="sm"
              variant="bordered"
              radius="sm"
              color={
                customerSubscription.status === "active" ? "primary" : "warning"
              }
            >
              {getStatusTranslation(customerSubscription.status)}
            </Chip>
            <Chip
              data-identifier="subscription-recurring"
              size="sm"
              variant="bordered"
              radius="sm"
            >
              <div className="flex items-center gap-1">
                {customerSubscription.isRecurring ? (
                  <Repeat className="h-3 w-3" />
                ) : (
                  <CreditCard className="h-3 w-3" />
                )}
                {customerSubscription.isRecurring
                  ? t`Ponavljajoči`
                  : t`Enkratni`}
              </div>
            </Chip>
            {customerSubscription.autoRenewOnVisitExhaustion && (
              <Chip
                data-identifier="subscription-auto-renew-on-exhaustion"
                size="sm"
                variant="bordered"
                radius="sm"
                color="default"
              >
                <Trans>Samodejna obnova ob porabi</Trans>
              </Chip>
            )}
            {hasActiveDiscount && discountChipLabel && (
              <Chip
                data-identifier="subscription-discount"
                size="sm"
                variant="bordered"
                radius="sm"
                color="success"
              >
                {discountChipLabel}
              </Chip>
            )}
            {companySplitChipLabel && (
              <Chip
                data-identifier="subscription-company-split"
                size="sm"
                variant="bordered"
                radius="sm"
                color="primary"
              >
                {companySplitChipLabel}
              </Chip>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end">
            {hasActiveDiscount &&
            discountedPriceFormatted &&
            discountedPriceFormatted !== customerSubscription.priceFormatted ? (
              <>
                <p
                  data-identifier="subscription-price"
                  className="text-lg font-semibold"
                >
                  {discountedPriceFormatted}
                </p>
                <p className="text-default-400 text-sm line-through">
                  {customerSubscription.priceFormatted}
                </p>
              </>
            ) : (
              <p
                data-identifier="subscription-price"
                className="text-lg font-semibold"
              >
                {customerSubscription.priceFormatted}
              </p>
            )}
          </div>
        </CardHeader>

        <CardBody className="flex flex-col gap-2">
          <div className="flex flex-col">
            <h3
              data-identifier="customer-subscription-name"
              className="text-xl font-semibold"
            >
              <span data-identifier="subscription-name">
                {customerSubscription.variation?.subscription?.name ??
                  customerSubscription.name ??
                  t`Paket po meri`}
              </span>
              {customerSubscription.variation?.subscription?.name &&
                customerSubscription.variation?.name && (
                  <>
                    <span> - </span>
                    <span data-identifier="subscription-variation">
                      {customerSubscription.variation.name}
                    </span>
                  </>
                )}
            </h3>

            {customerSubscription.customerSubscriptionServices.length > 0 && (
              <p
                data-identifier="subscription-service-names"
                className="text-small text-default-500"
              >
                <Trans>Vključene storitve</Trans>
                {": "}
                {customerSubscription.customerSubscriptionServices
                  .map((subscriptionService) =>
                    getLocalizedName(subscriptionService.service.nameLocalized),
                  )
                  .join(", ")}
              </p>
            )}
            {!customerSubscription.allLocations && (
              <p
                data-identifier="subscription-locations"
                className="text-small text-default-500"
              >
                <Trans>Lokacije</Trans>
                {": "}
                {usableLocationLabels.length > 0
                  ? usableLocationLabels.join(", ")
                  : t`nobena`}
              </p>
            )}
            <p
              data-identifier="subscription-duration"
              className="text-small text-default-500"
            >
              <Trans>Trajanje</Trans>
              {": "}
              {customerSubscription.durationValue}{" "}
              {getTranslatedTimeframe(customerSubscription.durationUnit)}
            </p>
          </div>

          <p
            data-identifier="subscription-validity"
            className="text-small text-default-500"
          >
            <Trans>Veljavnost</Trans>
            {": "}
            <span data-identifier="subscription-valid-from">
              {customerSubscription.validFromFormatted}
            </span>{" "}
            -{" "}
            <span data-identifier="subscription-valid-to">
              {customerSubscription.validToFormatted}
            </span>
          </p>
          {commitment && (
            <CustomerSubscriptionCommitmentTerm
              commitment={commitment}
              durationValue={customerSubscription.durationValue}
              durationUnit={customerSubscription.durationUnit}
            />
          )}
          <div className="flex flex-col gap-4">
            {hasAnyLimitations && <Divider />}
            {customerSubscription.customerSubscriptionServices
              .filter(
                (subscriptionService) =>
                  subscriptionService.limitations.length > 0,
              )
              .map((subscriptionService) => (
                <div key={subscriptionService.id} className="space-y-2">
                  <p
                    data-identifier="subscription-service-name"
                    className="text-medium font-semibold"
                  >
                    {getLocalizedName(
                      subscriptionService.service.nameLocalized,
                    )}
                  </p>
                  <div className="space-y-3">
                    {subscriptionService.limitations.map((limitation) => {
                      const {
                        usagePercentage,
                        totalBookedPercentage,
                        futureUsage,
                      } = getLimitationProgressValues(limitation);

                      return (
                        <div
                          key={`${subscriptionService.serviceId}-${limitation.timeframe}`}
                          className="space-y-1"
                        >
                          <div
                            data-identifier="subscription-service-limitation"
                            className="text-small flex items-center justify-between gap-2"
                          >
                            <span className="font-medium">
                              {getTranslatedLimitationTimeframe(
                                limitation.timeframe,
                              )}
                            </span>
                            <span
                              data-identifier="subscription-service-remaining"
                              className="text-default-500 shrink-0"
                            >
                              <Trans>
                                {limitation.usageAsOfNow}/
                                {limitation.limitAmount}
                              </Trans>
                            </span>
                          </div>
                          <div
                            data-identifier="subscription-service-progress"
                            className="bg-default-200 relative h-1.5 w-full overflow-hidden rounded-full"
                          >
                            <div
                              data-identifier="subscription-service-progress-booked"
                              className="absolute inset-y-0 left-0 bg-amber-500"
                              style={{ width: `${totalBookedPercentage}%` }}
                            />
                            <div
                              className="bg-success absolute inset-y-0 left-0"
                              style={{ width: `${usagePercentage}%` }}
                            />
                          </div>
                          <p className="text-default-500 text-xs">
                            <Trans>
                              {limitation.usageAsOfNow} opravljenih,{" "}
                              {futureUsage} rezerviranih
                            </Trans>
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            {hasTotalLimitations && (
              <div className="space-y-3">
                <p
                  className="text-medium font-semibold"
                  data-identifier="subscription-total-visits"
                >
                  {t`Skupni obiski`}
                </p>
                {customerSubscription.limitations.map((limitation) => {
                  const {
                    usagePercentage,
                    totalBookedPercentage,
                    futureUsage,
                  } = getLimitationProgressValues(limitation);

                  return (
                    <div
                      key={`limitation-${limitation.timeframe}`}
                      className="space-y-1"
                    >
                      <div
                        data-identifier="subscription-total-limitation"
                        className="text-small flex items-center justify-between gap-2"
                      >
                        <span className="font-medium">
                          {getTranslatedLimitationTimeframe(
                            limitation.timeframe,
                          )}
                        </span>
                        <span
                          data-identifier="subscription-total-remaining"
                          className="text-default-500 shrink-0"
                        >
                          <Trans>
                            {limitation.usageAsOfNow}/{
                              limitation.limitAmount
                            }{" "}
                          </Trans>
                        </span>
                      </div>
                      <div
                        data-identifier="subscription-total-progress"
                        className="bg-default-200 relative h-1.5 w-full overflow-hidden rounded-full"
                      >
                        <div
                          data-identifier="subscription-total-progress-booked"
                          className="absolute inset-y-0 left-0 bg-amber-500"
                          style={{ width: `${totalBookedPercentage}%` }}
                        />
                        <div
                          className="bg-success absolute inset-y-0 left-0"
                          style={{ width: `${usagePercentage}%` }}
                        />
                      </div>
                      <p className="text-default-500 text-xs">
                        <Trans>
                          {limitation.usageAsOfNow} opravljenih, {futureUsage}{" "}
                          rezerviranih
                        </Trans>
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            {hasAnyLimitations && <Divider />}
            <CustomerSubscriptionCreditsSection
              customerSubscriptionId={customerSubscription.id}
            />
            <Divider />
          </div>
          <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:gap-2">
            <div className="flex items-center gap-1">
              <Chip
                data-identifier="subscription-future-appointments-count"
                size="sm"
                variant="flat"
                radius="sm"
                color="primary"
              >
                {customerSubscription.futureAppointmentsCount}
              </Chip>
              <p
                className={"text-sm"}
                data-identifier="subscription-future-appointments"
              >
                <Trans>terminov v prihodnosti</Trans>
              </p>
            </div>
            <span className="hidden sm:inline"> · </span>
            <div className="flex items-center gap-1">
              <Chip
                data-identifier="subscription-current-validity-appointments-count"
                size="sm"
                variant="flat"
                radius="sm"
                color="primary"
              >
                {customerSubscription.currentValidityAppointmentCount}
              </Chip>
              <p
                className={"text-sm"}
                data-identifier="subscription-current-validity-appointments"
              >
                <Trans>terminov v tem obdobju paketa</Trans>
              </p>
            </div>
          </div>

          {customerSubscription.scheduledSubscriptionFreezes &&
            customerSubscription.scheduledSubscriptionFreezes.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 flex items-center gap-2">
                  <Snowflake className="h-4 w-4" />
                  <p className="text-sm font-medium">
                    <Trans>Načrtovane zamrznitve</Trans>
                  </p>
                </div>
                <div className="space-y-2">
                  {customerSubscription.scheduledSubscriptionFreezes.map(
                    (freeze) => {
                      const status = getFreezeStatusMeta(freeze.requestStatus);
                      return (
                        <div
                          key={freeze.id}
                          className="flex items-center justify-between rounded-md border p-2"
                          data-identifier="scheduled-freeze-item"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span
                                data-identifier="scheduled-freeze-dates"
                                className="text-sm font-medium"
                              >
                                {freeze.freezeStartFormatted} 00:00 -
                                {freeze.freezeEndFormatted} 23:59
                              </span>
                              <Chip
                                data-identifier="scheduled-freeze-reason"
                                size="sm"
                                variant="flat"
                                color="primary"
                                radius="sm"
                              >
                                {freeze.freezeReason === "Sickness"
                                  ? t`Bolezen`
                                  : freeze.freezeReason === "Vacation"
                                    ? t`Dopust`
                                    : t`Drugo`}
                              </Chip>
                              <Chip
                                data-identifier="scheduled-freeze-status"
                                size="sm"
                                variant="flat"
                                color={status.color}
                                radius="sm"
                              >
                                {status.label}
                              </Chip>
                            </div>
                            <span className="text-muted-foreground text-xs">
                              {freeze.endedEarlyAt &&
                              freeze.originalFreezeEnd ? (
                                <Trans>
                                  Stranka je zamrznitev končala predčasno
                                  (prvotno do{" "}
                                  {dayjs(freeze.originalFreezeEnd).format(
                                    "DD. MM. YYYY",
                                  )}
                                  )
                                </Trans>
                              ) : freeze.requestStatus ===
                                "cancelled" ? null : dayjs(
                                  freeze.freezeStart,
                                ).isSame(dayjs(), "day") ? (
                                <Trans>Začne danes</Trans>
                              ) : dayjs(freeze.freezeStart).isBefore(
                                  dayjs(),
                                ) ? (
                                <Trans>V teku</Trans>
                              ) : (
                                <Trans>
                                  Začne čez{" "}
                                  {dayjs(freeze.freezeStart).diff(
                                    dayjs(),
                                    "days",
                                  )}{" "}
                                  dni
                                </Trans>
                              )}
                            </span>
                            {freeze.notes && (
                              <span className="text-muted-foreground text-xs">
                                {freeze.notes}
                              </span>
                            )}
                          </div>
                          <div
                            className={cn(
                              "flex items-center gap-1",
                              freeze.requestStatus === "cancelled" && "hidden",
                            )}
                          >
                            <Button
                              isIconOnly
                              size="sm"
                              variant="flat"
                              color="primary"
                              data-identifier="edit-scheduled-freeze-btn"
                              onPress={() => {
                                setScheduledFreezeId(freeze.id);
                                onFreezeModalOpen();
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="flat"
                              color="danger"
                              data-identifier="delete-scheduled-freeze-btn"
                              onPress={() =>
                                setSelectedFreezeForDelete(freeze.id)
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}
        </CardBody>
        <CardFooter className="flex flex-col gap-3 pt-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {customerSubscription.variation?.subscription?.tags.map((tag) => (
              <Chip
                key={tag.id}
                data-identifier="subscription-tag"
                size="sm"
                variant="flat"
                radius="sm"
              >
                {getLocalizedValue(tag.nameLocalized)}
              </Chip>
            ))}
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="bordered"
                  size="sm"
                  className="w-full sm:w-auto"
                  data-identifier="subscription-appointments-dropdown-trigger"
                  startContent={<Calendar className="h-4 w-4" />}
                  isLoading={isPostingPrescheduled}
                >
                  <Trans>Termini</Trans>
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Appointment actions">
                <DropdownItem
                  key="add"
                  data-identifier="subscription-add-appointment-action"
                  onPress={onPrescheduledSelectOpen}
                >
                  <Trans>Dodaj na skupinski termin</Trans>
                </DropdownItem>
                <DropdownItem
                  key="link"
                  data-identifier="subscription-link-appointments-action"
                  onPress={onLinkAppointmentsModalOpen}
                >
                  <Trans>Poveži obstoječe termine</Trans>
                </DropdownItem>
                <DropdownItem
                  key="list"
                  data-identifier="subscription-list-appointments-action"
                  onPress={onAppointmentsModalOpen}
                >
                  <Trans>Seznam terminov</Trans>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Button
              variant="bordered"
              size="sm"
              className="w-full sm:w-auto"
              data-identifier="invoice-modal-open-button"
              onPress={() =>
                setInvoiceModalSubscriptionId(customerSubscription.id)
              }
              startContent={<CreditCard className="h-4 w-4" />}
            >
              <Trans>Obdobja</Trans>
            </Button>
            <Button
              variant="bordered"
              size="sm"
              className="w-full sm:w-auto"
              onPress={() =>
                setEventsModalSubscriptionId(customerSubscription.id)
              }
              startContent={<Clock className="h-4 w-4" />}
            >
              <Trans>Spremembe</Trans>
            </Button>
            <Button
              variant="bordered"
              size="sm"
              className="w-full sm:w-auto"
              data-identifier="subscription-notifications-button"
              onPress={() =>
                setNotificationsModalSubscriptionId(customerSubscription.id)
              }
              startContent={<Bell className="h-4 w-4" />}
            >
              <Trans>Obvestila</Trans>
            </Button>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="bordered"
                  size="sm"
                  className="w-full sm:w-auto"
                  data-identifier="subscription-actions-dropdown-trigger"
                  startContent={<Settings className="h-4 w-4" />}
                >
                  <Trans>Nastavitve</Trans>
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Subscription actions">
                <DropdownItem
                  key="settings"
                  data-identifier="subscription-settings-action"
                  onPress={onSettingsModalOpen}
                >
                  <Trans>Nastavitve paketa</Trans>
                </DropdownItem>

                <DropdownItem
                  key="extend"
                  data-identifier="subscription-extend-action"
                  description={
                    isCurrentPeriodCommitted
                      ? t`Trenutno obdobje je del vezave.`
                      : undefined
                  }
                  onPress={onExtendModalOpen}
                >
                  <Trans>Spremeni datum veljavnosti paketa</Trans>
                </DropdownItem>

                <DropdownItem
                  key="freeze"
                  data-identifier="subscription-freeze-action"
                  onPress={() => {
                    setScheduledFreezeId(null);
                    onFreezeModalOpen();
                  }}
                >
                  <Trans>Zamrzni paket</Trans>
                </DropdownItem>

                {canOpenCommitments ? (
                  <DropdownItem
                    key="commitments"
                    data-identifier="subscription-commitments-action"
                    onPress={onCommitmentsModalOpen}
                  >
                    <Trans>Vezave</Trans>
                  </DropdownItem>
                ) : null}

                {isSubscriptionCanceled ? (
                  <DropdownItem
                    key="reactivate"
                    data-identifier="subscription-reactivate-action"
                    onPress={onReactivateModalOpen}
                  >
                    <Trans>Ponovno aktiviraj paket</Trans>
                  </DropdownItem>
                ) : (
                  <DropdownItem
                    key="cancel"
                    data-identifier="subscription-cancel-action"
                    className="text-danger"
                    color="danger"
                    onPress={onCancelModalOpen}
                  >
                    <Trans>Prekliči paket</Trans>
                  </DropdownItem>
                )}
                <DropdownItem
                  key="delete"
                  className="text-danger"
                  color="danger"
                  onPress={onDeleteModalOpen}
                >
                  <Trans>Izbriši paket</Trans>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </CardFooter>
      </Card>

      <PrescheduledSelectModal
        isOpen={isPrescheduledSelectOpen}
        attendeeCustomerId={customerSubscription.customerId}
        overrideServiceIds={
          subscriptionServiceIds.length > 0 ? subscriptionServiceIds : undefined
        }
        overrideDateRange={{
          start: parseDate(
            dayjs(customerSubscription.validFrom).format("YYYY-MM-DD"),
          ),
          end: customerSubscription.isRecurring
            ? undefined
            : parseDate(
                dayjs(customerSubscription.validTo).format("YYYY-MM-DD"),
              ),
        }}
        onClose={async (selectedIds?: number[]) => {
          onPrescheduledSelectClose();
          if (!selectedIds || selectedIds.length === 0) return;

          try {
            await postPrescheduledUserAppointment({
              customer: {
                customerId: customerSubscription.customerId,
                name: "",
                lastName: "",
              },
              appointmentIds: selectedIds,
              notificationSettings: [],
              customerSubscriptionId: customerSubscription.id,
            });
            addToast({
              title: t`Termin uspešno dodan`,
              color: "success",
            });
            queryClient.invalidateQueries({
              queryKey: ["customer-subscription"],
            });
          } catch (error) {
            addToast({
              title: t`Napaka pri dodajanju termina`,
              description: prescheduledAppointmentError,
              color: "danger",
            });
          }
        }}
        customerSubscriptionId={customerSubscription.id}
      />

      <CustomerSubscriptionExtensionModal
        isOpen={isExtendModalOpen}
        onClose={onExtendModalClose}
        customerSubscriptionId={customerSubscription.id}
      />

      <CustomerSubscriptionFreezeModal
        isOpen={isFreezeModalOpen}
        onClose={() => {
          onFreezeModalClose();
          setScheduledFreezeId(null);
        }}
        customerSubscriptionId={customerSubscription.id}
        scheduledFreezeId={scheduledFreezeId}
      />

      <SubscriptionInvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setInvoiceModalSubscriptionId(null)}
        customerSubscriptionId={customerSubscription.id}
      />

      <CustomerSubscriptionSettingsModal
        isOpen={settingsModalOpen}
        onClose={() => {
          onSettingsModalClose();
        }}
        customerSubscriptionId={customerSubscription.id}
      />

      <CustomerSubscriptionReactivateModal
        isOpen={isReactivateModalOpen}
        onClose={onReactivateModalClose}
        customerSubscriptionId={customerSubscription.id}
      />
      <CustomerSubscriptionCancelModal
        isOpen={isCancelModalOpen}
        onClose={onCancelModalClose}
        customerSubscriptionId={customerSubscription.id}
      />

      <CustomerSubscriptionCommitmentsModal
        isOpen={isCommitmentsModalOpen}
        onClose={onCommitmentsModalClose}
        customerSubscription={customerSubscription}
      />

      <LimeModal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Brisanje paketa</Trans>
          </ModalHeader>
          <ModalBody>
            {canDeleteSubscription ? (
              <p>
                <Trans>Ali ste prepričani, da želite izbrisati ta paket?</Trans>
              </p>
            ) : (
              <p>
                <Trans>Trajno izbrišete lahko samo preklicane pakete.</Trans>
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={onDeleteModalClose}
              isDisabled={isDeletingSubscription}
            >
              <Trans>Zapri</Trans>
            </Button>
            {canDeleteSubscription && (
              <Button
                color="danger"
                variant="solid"
                onPress={handleDeleteSubscription}
                isLoading={isDeletingSubscription}
              >
                <Trans>Potrdi brisanje</Trans>
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </LimeModal>

      <CustomerSubscriptionEventsModal
        isOpen={isEventsModalOpen}
        onClose={() => setEventsModalSubscriptionId(null)}
        customerSubscriptionId={customerSubscription.id}
        subscriptionName={customerSubscription.variation?.subscription?.name}
        variationName={customerSubscription.variation?.name}
      />

      {isNotificationsModalOpen ? (
        <CustomerSubscriptionNotificationsModal
          isOpen
          onClose={() => setNotificationsModalSubscriptionId(null)}
          customerSubscriptionId={customerSubscription.id}
        />
      ) : null}

      <CustomerSubscriptionDeleteFreezeModal
        isOpen={selectedFreezeForDelete !== null}
        onClose={() => setSelectedFreezeForDelete(null)}
        customerSubscriptionId={customerSubscription.id}
        scheduledFreezeId={selectedFreezeForDelete}
      />

      <CustomerSubscriptionLinkAppointmentsModal
        isOpen={isLinkAppointmentsModalOpen}
        onClose={onLinkAppointmentsModalClose}
        customerSubscription={customerSubscription}
      />

      <CustomerSubscriptionAppointmentsModal
        isOpen={isAppointmentsModalOpen}
        onClose={onAppointmentsModalClose}
        customerSubscriptionId={customerSubscription.id}
      />
    </>
  );
};

export const CustomerSubscriptionCard = ({
  customerSubscriptionId,
}: {
  customerSubscriptionId: number;
}) => {
  const { data, isLoading, processedErrorMessage } =
    api.customer.useGetCustomerSubscription({ customerSubscriptionId });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-1/2 rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  if (processedErrorMessage || !data) {
    return (
      <div className="py-6 text-center text-sm text-danger">
        {processedErrorMessage ?? t`Napaka pri nalaganju paketa.`}
      </div>
    );
  }

  return (
    <CustomerSubscriptionCardContent
      customerSubscription={data.customerSubscription}
    />
  );
};
